import type { EventContext } from './createEventingPayloadsDE';
import { publishedConfigVersion } from '../events/stepCorrelation';
import { EventingLocation } from '../types/enums';
import { uuid } from '../shared/utils';

interface ResultsEventContext {
  eventContext: EventContext;
  listId?: string | undefined;
  presets?: {
    degree?: string | null;
    category?: string | null;
    subject?: string | null;
  };
  location?: string;
  productClickedCorrelationId?: string;
  viewCorrelationId?: string;
  brand?: string;
  position?: number;
  capId?: number;
  programId?: number;
  outboundUrl?: string;
  isProgramSubmitted?: boolean;
  isUserRequested?: boolean;
  /**
   * DSL result-step config id (`dslConfig.configId`). Supplied only on
   * the governed DSL result path (the surrounding `TrackingProvider`
   * carries it); the imperative `ResultsPage` passes nothing so the
   * emitted product payloads stay byte-identical.
   */
  dslConfigId?: string;
  /**
   * Published config version. An integer of at least 1 is copied onto
   * the context; `0` and non-integers are omitted.
   */
  dslConfigVersion?: number;
}

export const createResultsEventContext = ({
  eventContext,
  listId,
  presets,
  location,
  productClickedCorrelationId,
  viewCorrelationId,
  brand,
  position,
  capId,
  programId,
  outboundUrl,
  isProgramSubmitted,
  isUserRequested,
  dslConfigId,
  dslConfigVersion,
}: ResultsEventContext) => {
  const publishedVersion = publishedConfigVersion(dslConfigVersion);
  const formatSubtype = () => {
    const step = eventContext.currentStep as any;
    if (step?.resultType) {
      return `dexp-${step.resultType.replace(/_/g, '-').toLowerCase()}`;
    } else if (step?.componentType) {
      return `dexp-${step.componentType.replace(/_/g, '-').toLowerCase()}`;
    }
    return 'dexp-results';
  };

  // Presets override dcs: when presets are provided, use them for degree/category/subject
  const dcs =
    presets != null
      ? {
          degree: presets.degree ?? eventContext.dcs?.degree ?? '',
          category: presets.category ?? eventContext.dcs?.category ?? '',
          subject: presets.subject ?? eventContext.dcs?.subject ?? '',
        }
      : eventContext.dcs;

  return {
    ...eventContext,
    dcs,
    formatType: 'dexp-offering',
    formatSubtype: formatSubtype(),
    formName: 'dexp-hosted-lead-form',
    stepName: 'pii-confirmation',
    location: location || EventingLocation.CONVERSION_DRAWER,
    stepType: 'LEAD',
    listId: listId,
    customDimensions: [
      {
        key: 'sourceCorrelationId',
        value: eventContext.formViewedCorrelationId || '',
      },
      {
        key: 'isUserRequested',
        value: String(isUserRequested),
      },
      {
        key: 'flowId',
        value: eventContext.currentStep?.flowId || '',
      },
      // Imperative path uses Offerings Service match; DSL path stamps per row.
      {
        key: 'resultsSource',
        value: '/v1/offerings/search/match',
      },
    ],
    presets: presets
      ? {
          degree: presets.degree ?? undefined,
          category: presets.category ?? undefined,
          subject: presets.subject ?? undefined,
        }
      : undefined,
    productClickedCorrelationId: productClickedCorrelationId || uuid(),
    viewCorrelationId,
    brand,
    position,
    sku: capId ? String(capId) : undefined,
    productId: programId ? String(programId) : undefined,
    outboundUrl,
    actionOutcome: isProgramSubmitted ? 'application' : 'explore',
    ...(dslConfigId ? { dslConfigId } : {}),
    ...(publishedVersion !== undefined
      ? { dslConfigVersion: publishedVersion }
      : {}),
  };
};
